// 按需引入场景下，模板组件会自动带样式；Message / MessageBox 等指令式 API 需手动引入样式
// Dialog / Overlay 样式提前打入主包，避免首次打开 Dialog 时懒加载 CSS 覆盖 element.scss 中的布局修正
// Tree：views 里手写 `import { ElTree } from "element-plus"` 不会经 unplugin 注入样式，
// 缺省会导致展开箭头 transform 丢失（始终朝右）
import "element-plus/es/components/overlay/style/css";
import "element-plus/es/components/dialog/style/css";
import "element-plus/es/components/drawer/style/css";
import "element-plus/es/components/input/style/css";
import "element-plus/es/components/radio/style/css";
import "element-plus/es/components/radio-button/style/css";
import "element-plus/es/components/alert/style/css";
import "element-plus/es/components/message/style/css";
import "element-plus/es/components/message-box/style/css";
import "element-plus/es/components/notification/style/css";
import "element-plus/es/components/loading/style/css";
import "element-plus/es/components/tree/style/css";
import "element-plus/es/components/tree-select/style/css";

import { markRaw } from "vue";
import { WarnTriangleFilled, WarningFilled } from "@element-plus/icons-vue";
import { ElButton, ElDialog, ElInputNumber, ElMessageBox } from "element-plus";
import { TypeComponentsMap } from "element-plus/es/utils/vue/icon";
import type { AppContext } from "vue";
import type { ElMessageBoxOptions, ElMessageBoxShortcutMethod } from "element-plus";
import IconfontLoading from "@shared/components/IconfontLoading/index.vue";

// ============================================================
// ElAlert 全局默认图标（Figma node 158:2）
// - warning：WarnTriangleFilled
// - info / primary：WarningFilled（替换默认 InfoFilled）
// ============================================================
TypeComponentsMap.warning = markRaw(WarnTriangleFilled);
TypeComponentsMap.info = markRaw(WarningFilled);
TypeComponentsMap.primary = markRaw(WarningFilled);

// ============================================================
// ElDialog 全局默认（三端 main.ts 均 import 本文件）
// - closeOnClickModal: 点击遮罩不关闭
// - appendToBody: 不挂 body，随页面组件树走，keep-alive 切页签时一并隐藏
// 业务需挂 body 时显式 append-to-body
// ============================================================
ElDialog.props.closeOnClickModal.default = false;
ElDialog.props.appendToBody.default = false;
ElDialog.props.alignCenter.default = false;

// ============================================================
// ElInputNumber 全局默认：文字左对齐（与 el-input / 可售库存百分比一致）
// 确需居中时业务显式传 align="center"
// ============================================================
ElInputNumber.props.align.default = "left";

// ============================================================
// ElButton 全局默认 loading 图标（iconfont icon-loading）
// ============================================================
ElButton.props.loadingIcon.default = markRaw(IconfontLoading);

// ============================================================
// ElMessageBox 全局默认
// - closeOnClickModal: 点击遮罩不关闭
// - appendTo: 当前页根节点（.app-main 下第一个子元素），切页签随 DOM 卸掉，不 router 强制 close
// 布局级弹窗可显式 appendTo: document.body
// ============================================================

/** 解析当前业务页挂载点：AppMain 内 router-view 输出的页面根节点 */
function resolvePageAppendTo(): HTMLElement | string {
    const main = document.querySelector(".app-main");
    if (!main) return document.body;

    const pageRoot = main.firstElementChild;
    if (pageRoot instanceof HTMLElement) return pageRoot;

    return main instanceof HTMLElement ? main : document.body;
}

function mergeMessageBoxOptions(options?: ElMessageBoxOptions): ElMessageBoxOptions {
    // MessageBox warning 用圆形 WarningFilled（设计稿）；Alert 仍走 TypeComponentsMap 的 WarnTriangleFilled
    const warningIcon = options?.type === "warning" && options.icon == null ? { icon: markRaw(WarningFilled) } : {};

    return {
        ...options,
        ...warningIcon,
        closeOnClickModal: options?.closeOnClickModal ?? false,
        appendTo: options?.appendTo ?? resolvePageAppendTo(),
    };
}

function wrapMessageBoxMethod(method: ElMessageBoxShortcutMethod): ElMessageBoxShortcutMethod {
    const wrapped: ElMessageBoxShortcutMethod = ((
        message: ElMessageBoxOptions["message"],
        title?: ElMessageBoxOptions["title"] | ElMessageBoxOptions,
        options?: ElMessageBoxOptions,
        appContext?: AppContext | null,
    ) => {
        if (title !== null && typeof title === "object") {
            return method(message, "", mergeMessageBoxOptions(title), appContext);
        }
        return method(message, title as ElMessageBoxOptions["title"], mergeMessageBoxOptions(options), appContext);
    }) as ElMessageBoxShortcutMethod;

    return wrapped;
}

ElMessageBox.confirm = wrapMessageBoxMethod(ElMessageBox.confirm.bind(ElMessageBox));
ElMessageBox.alert = wrapMessageBoxMethod(ElMessageBox.alert.bind(ElMessageBox));
ElMessageBox.prompt = wrapMessageBoxMethod(ElMessageBox.prompt.bind(ElMessageBox));
